import { useMemo, useState } from 'react';

import { useAppSelector } from '@/configuration/setup/hooks';
import {
    getAssetTreeAccountIds,
    getAssetTreeAssetIds,
    getAssetTreeInitialized,
    getAssetTreeWorkshopIds,
} from '@/features/app/assetTreeSlice';
import InternalErrorState from '@/features/app/InternalErrorState';
import { useGetEngineOilListQuery } from '@/services/api';
import EngineOilTable from './EngineOilTable';
import { PAGE_SIZE_OPTIONS } from './Pagination';
import { mapToRows } from './rowMapper';
import { ENGINE_OIL_TABLE_SORT_KEY, type EngineOilSortBy, type SortOrder } from './types';

const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const EngineOilTablePage = () => {
    const assetIds = useAppSelector(getAssetTreeAssetIds);
    const accountIds = useAppSelector(getAssetTreeAccountIds);
    const workshopIds = useAppSelector(getAssetTreeWorkshopIds);
    const initialized = useAppSelector(getAssetTreeInitialized);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortBy, setSortBy] = useState<EngineOilSortBy>(ENGINE_OIL_TABLE_SORT_KEY.VEHICLE_NAME);
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    const queryKey = JSON.stringify([assetIds, accountIds, workshopIds, sortBy, sortOrder, pageSize]);
    const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
    if (queryKey !== prevQueryKey) {
        setPrevQueryKey(queryKey);
        setPage(1);
    }

    const { data, isFetching, isError, refetch } = useGetEngineOilListQuery(
        {
            asset_ids: assetIds,
            account_ids: accountIds,
            workshop_ids: workshopIds,
            page,
            size: pageSize,
            sort_by: sortBy,
            sort_order: sortOrder,
        },
        { skip: !initialized },
    );

    const rows = useMemo(() => mapToRows(data?.items ?? []), [data]);
    const totalPages = data?.totalPages ?? 0;

    const handleSort = (key: EngineOilSortBy) => {
        if (key === sortBy) {
            setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(key);
            setSortOrder('asc');
        }
    };

    return (
        <div className="padding-20">
            {isError ? (
                <InternalErrorState reloadTriggerFunction={refetch} />
            ) : (
                <EngineOilTable
                    rows={rows}
                    inProgress={isFetching}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    page={page}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    assetIds={assetIds}
                    accountIds={accountIds}
                    workshopIds={workshopIds}
                />
            )}
        </div>
    );
};

export default EngineOilTablePage;
